import type { Copy } from '../../types/copy'

export const enCopy: Copy = {
  language: 'en',
  badge: 'Brave Mind Program',
  access: {
    headline: 'Your personalized experience starts here 💜',
    subtext: "Enter your details to start creating your child's program.",
    nameLabel: 'Your name',
    namePlaceholder: 'Laura',
    emailLabel: 'Email address used for your purchase',
    emailPlaceholder: 'laura@email.com',
    submit: 'Access my program →',
    footnote: 'This will only take a few minutes.',
    errorName: 'Please enter your name to continue.',
    errorEmail: 'Please check the email you used for your purchase.',
  },
  preparing: {
    loading: 'Getting your experience ready…',
    ready: 'All set 💜',
  },
  welcome: {
    greeting: (parentName) => `Welcome, ${parentName} 💜`,
    fallbackName: 'there',
    headline: "Now let's create something just for your little one.",
    subtext:
      "We'd love to know your child a little better so we can personalize their 5 audio experiences.",
    highlights: [
      { emoji: '⏱️', text: 'Takes about 5 minutes' },
      { emoji: '🎧', text: '5 personalized experiences' },
      { emoji: '✨', text: "We'll take care of the rest" },
      { emoji: '💜', text: "You'll receive everything by email" },
    ],
    cta: 'Start personalizing →',
  },
  quiz: {
    stepOf: (step, total) => `Step ${step} of ${total}`,
    backLabel: 'Go back to the previous question',
    continue: 'Continue →',
    seeSummary: 'Review summary →',
  },
  steps: {
    name: {
      headline: "What's your little one's name? 💜",
      subtext: "We'll use their name inside the audio experiences.",
      label: 'Name or nickname',
      placeholder: 'Emma',
      genderLegend: 'Is this your son or daughter?',
      son: 'Son',
      daughter: 'Daughter',
    },
    age: {
      headline: (childName) => `How old is ${childName}?`,
      subtext: 'This helps us shape the language and tone of the audios.',
    },
    interests: {
      headline: (childName) => `What does ${childName} love? ✨`,
      subtext: 'You can choose as many as you like.',
      otherLabel: 'Tell us what they love',
      otherPlaceholder: "They're fascinated by vintage trains",
    },
    personality: {
      headline: (childName) => `How would you describe ${childName}?`,
      subtext: (max) => `Choose up to ${max}.`,
    },
    priorities: {
      headline: (childName) => `What would you like to help ${childName} grow?`,
      subtext: (max) => `Select up to ${max} options.`,
    },
    context: {
      headline: 'Tell us a little more 💜',
      question: 'When would you like these experiences to be there for them?',
      contextLabel: 'If you like, share a brief moment. (optional)',
      contextPlaceholder:
        "For example: when something doesn't work on the first try, they get frustrated and say they can't do it.",
    },
    personal: {
      headline: 'Here comes the most special part ✨',
      question: (childName) =>
        `Is there something unique about ${childName} you'd like woven into their experiences?`,
      detailPlaceholder:
        'For example: they love building things with their grandpa, they dream of traveling to space, or they are learning to ride a bike.',
      messageLabel: "What message would you like them to remember? (optional)",
      messagePlaceholder:
        'I want them to remember that we believe in them and that they can do big things.',
    },
  },
  summary: {
    headline: (childName) => `Here's what we learned about ${childName} 💜`,
    genderRow: 'Son or daughter',
    ageRow: 'Age',
    interestsRow: 'Interests',
    personalityRow: 'Personality',
    prioritiesRow: 'Strengths to nurture',
    ageValue: (age) => `${age} years old`,
    note: (childName) =>
      `With this, we can create experiences that feel truly made for ${childName}.`,
    consent:
      'I confirm that I am the parent or legal guardian and I authorize the use of this information to create the personalized product I purchased.',
    submit: (childName) => `Create ${childName}'s program ✨`,
    sending: 'Sending…',
    edit: 'Edit my answers',
    networkError: "We couldn't send the profile. Please check your connection and try again.",
  },
  processing: {
    first: (childName) => `Preparing ${childName}'s profile…`,
    second: 'Organizing their information…',
    third: 'All set 💜',
  },
  final: {
    badge: 'Profile received',
    headline: 'Now the magic begins ✨',
    subtext: (childName) =>
      `We have everything we need to create ${childName}'s 5 personalized experiences.`,
    timeline: [
      'Profile received',
      'Personalized creation',
      'Audio production',
      'Final review',
      'Delivery by email',
    ],
    emailNote: "You'll receive everything at the email you used to sign in",
    eta: 'Estimated time: 3 to 5 business days.',
    card: "There's nothing else you need to do.\nWe'll take care of the rest 💜",
    cta: 'Finish',
  },
  closed: {
    heading: (parentName) => `Thank you, ${parentName}`,
    fallbackName: 'truly',
    text: (childName, email) =>
      `You can close this window now. We'll email you at ${email} as soon as ${childName}'s experiences are ready.`,
  },
  options: {
    interests: {
      dinosaurs: 'Dinosaurs',
      robots: 'Robots',
      space: 'Space',
      vehicles: 'Vehicles',
      animals: 'Animals',
      drawing: 'Drawing',
      sports: 'Sports',
      nature: 'Nature',
      puzzles: 'Puzzles',
      stories: 'Stories',
      fantasy: 'Fantasy',
      music: 'Music',
      princesses: 'Princesses',
      fairies: 'Fairies',
      other: 'Something different',
    },
    personality: {
      curious: 'Very curious',
      imaginative: 'Imaginative',
      sensitive: 'Sensitive',
      energetic: 'Full of energy',
      calm: 'Calm',
      social: 'Sociable',
      brave: 'Brave',
      shy: 'Shy at first',
      determined: 'Determined',
      thoughtful: 'Thoughtful',
      independent: 'Independent',
      affectionate: 'Affectionate',
    },
    priorities: {
      confidence: { title: 'Confidence', description: 'Believing more in themselves.' },
      calm: { title: 'Calm', description: 'Finding steadiness when something frustrates them.' },
      perseverance: {
        title: 'Perseverance',
        description: 'Trying again when something feels hard.',
      },
      focus: { title: 'Focus', description: 'Concentrating better and finishing what they start.' },
      creativity: {
        title: 'Creativity',
        description: 'Trusting their ideas and finding new solutions.',
      },
    },
    situations: {
      not_working: "When something doesn't work out",
      frustrated: 'When they get frustrated',
      afraid_to_try: 'When they are afraid to try',
      learning_new: 'When they learn something new',
      loses_focus: 'When they lose focus',
      gives_up: 'When they want to give up quickly',
      self_doubt: 'When they doubt themselves',
      none: 'Nothing in particular',
    },
  },
}
